import { clamp } from './normalizeViewportConfig';

export type ViewportObserverCallback = (
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver
) => void;

export type ViewportObserverOptions = {
  readonly rootMargin: string;
  readonly thresholds: readonly number[];
};

export type ViewportObserverResult =
  | {
      readonly available: true;
      readonly observer: IntersectionObserver;
      readonly error: undefined;
    }
  | {
      readonly available: false;
      readonly observer: undefined;
      readonly error: string | undefined;
    };

/** SSR-safe: nunca referenciar `IntersectionObserver` fora de um typeof check. */
export function isIntersectionObserverAvailable(): boolean {
  return typeof IntersectionObserver !== 'undefined';
}

/** Deriva thresholds únicos e ordenados a partir de todos os targets: 0, enter/exit de cada, 1. */
export function buildThresholds(
  targets: readonly {
    readonly enterThreshold: number;
    readonly exitThreshold: number;
  }[]
): number[] {
  const unique = new Set<number>([0, 1]);

  for (const target of targets) {
    unique.add(clamp(target.enterThreshold, 0, 1));
    unique.add(clamp(target.exitThreshold, 0, 1));
  }

  return Array.from(unique).sort((a, b) => a - b);
}

/**
 * Cria um `IntersectionObserver` isolado (uma instância por `ViewportTracker`).
 * Nunca cria polling como fallback: se indisponível, retorna `available: false`.
 */
export function createIntersectionObserver(
  callback: ViewportObserverCallback,
  options: ViewportObserverOptions
): ViewportObserverResult {
  if (!isIntersectionObserverAvailable()) {
    return {
      available: false,
      observer: undefined,
      error: 'IntersectionObserver unavailable',
    };
  }

  try {
    const observer = new IntersectionObserver(callback, {
      root: null,
      rootMargin: options.rootMargin,
      threshold: options.thresholds as number[],
    });
    return { available: true, observer, error: undefined };
  } catch (error) {
    return {
      available: false,
      observer: undefined,
      error:
        error instanceof Error
          ? error.message
          : 'IntersectionObserver init failed',
    };
  }
}
